











































import {
  computed,
  defineComponent,
  onMounted,
  ref,
  watch
} from '@nuxtjs/composition-api';
import { SfInput } from '@storefront-ui/vue';
import debounce from 'lodash/debounce';
import { extend, ValidationProvider } from 'vee-validate';
import { required } from 'vee-validate/dist/rules';

import { useConfig, useStore } from '~/composables';
import { usePhoneMask } from '~/diptyqueTheme/composable/usePhoneMask';
import { phoneRegex, phoneRegexJP } from '~/diptyqueTheme/helpers/regexes';

extend('required', {
  ...required,
  message: 'This field is required'
});

extend('phone', {
  validate(value) {
    return {
      valid: phoneRegex.test(value.replace(/\D/g, ''))
    };
  }
});

extend('phone-jp', {
  message:
    'Please enter only half-width numbers without hyphens and up to 11 digits.',
  validate(value) {
    return {
      valid: phoneRegexJP.test(value.replace(/ /g, ''))
    };
  }
});

export default defineComponent({
  name: 'VaimoPhone',
  components: {
    SfInput,
    VaimoPhoneInput: () => import('organisms/VaimoPhoneInput.vue'),
    ValidationProvider
  },
  props: {
    value: {
      required: true,
      type: [String, null]
    },
    autocomplete: {
      required: false,
      type: String,
      default: 'tel'
    },
    classes: {
      required: false,
      type: String,
      default: ''
    }
  },
  emits: ['input', 'isPhoneValid', 'init', 'validation'],
  setup(props, { emit }) {
    const { isJpStore } = useStore();
    const { config } = useConfig();
    const fieldTouched = ref(false);
    const { clearMask } = usePhoneMask();
    const ALIGNMENT_CLASS = 'input--with-label';

    const isPhoneMaskWithFlagEnabled = computed(
      () => config.value.phone_mask_with_flag_enabled
    );

    const localVmodel = computed({
      get: () => props.value,
      set: (newValue) => {
        const clearedVal = isJpStore.value
          ? clearMask(newValue)
          : `+${clearMask(newValue)}`;
        emit('input', clearedVal);
      }
    });

    const inputClasses = computed(() => {
      if (props.classes.includes(ALIGNMENT_CLASS)) {
        return props.classes;
      }
      return [props.classes, ALIGNMENT_CLASS].join(' ');
    });

    const VALIDATE_DEBOUNCE = 300;

    const onValidate = debounce((value: boolean | { valid: boolean }) => {
      const valid = typeof value === 'boolean' ? value : value?.valid;
      emit('isPhoneValid', valid);
      emit('validation', valid);
    }, VALIDATE_DEBOUNCE);

    const onFocus = () => {
      fieldTouched.value = true;
      emit('init');
    };

    const onPhoneInputInit = () => {
      emit('init');
    };

    const plainPhoneProvider = ref<InstanceType<
      typeof ValidationProvider
    > | null>(null);

    watch(
      () => plainPhoneProvider.value?.flags?.pending,
      (isPending) => {
        if (isPending === false) {
          const isValid = plainPhoneProvider.value?.flags?.valid;
          onValidate(isValid);
        }
      }
    );

    onMounted(() => {
      if (!isPhoneMaskWithFlagEnabled.value && isJpStore.value) {
        emit('init');
      }
    });

    const phoneInputRef = ref(null);

    const resetValidation = () => {
      if (isPhoneMaskWithFlagEnabled.value) {
        phoneInputRef.value?.resetValidation();
      } else {
        plainPhoneProvider.value?.reset();
      }
    };

    return {
      localVmodel,
      isPhoneMaskWithFlagEnabled,
      isJpStore,
      onValidate,
      onFocus,
      onPhoneInputInit,
      plainPhoneProvider,
      phoneInputRef,
      resetValidation,
      inputClasses
    };
  }
});
